import React, { useState, useEffect } from 'react';
import { applicationService } from '../../services/api';
import { getUserData } from '../../utils/auth';
import './CompanyApplications.css';

function CompanyApplications() {
  const userData = getUserData();
  const companyId = userData?.companyId || userData?.CompanyId;

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesValue, setNotesValue] = useState('');
  const [shortlistingId, setShortlistingId] = useState(null);
  const [shortlistRemarks, setShortlistRemarks] = useState('');

  useEffect(() => {
    if (companyId) loadData();
    else setLoading(false);
  }, [companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await applicationService.getApplicationsByCompany(companyId);
      setApplications(data);
    } catch (error) {
      alert('Error loading applications: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShortlistClick = (app) => {
    if (app.IsShortlisted) {
      removeShortlist(app);
    } else {
      setShortlistingId(app.Id);
      setShortlistRemarks(app.CompanyNotes || '');
    }
  };

  const confirmShortlist = async () => {
    if (shortlistingId == null) return;
    const app = applications.find((a) => a.Id === shortlistingId);
    if (!app) return;
    try {
      await applicationService.updateApplicationReview({
        Id: app.Id,
        IsShortlisted: true,
        CompanyNotes: shortlistRemarks,
      });
      setShortlistingId(null);
      setShortlistRemarks('');
      loadData();
    } catch (error) {
      alert('Error shortlisting: ' + error.message);
    }
  };

  const cancelShortlist = () => {
    setShortlistingId(null);
    setShortlistRemarks('');
  };

  const removeShortlist = async (app) => {
    try {
      await applicationService.updateApplicationReview({
        Id: app.Id,
        IsShortlisted: false,
        CompanyNotes: app.CompanyNotes || '',
      });
      loadData();
    } catch (error) {
      alert('Error removing shortlist: ' + error.message);
    }
  };

  const startEditNotes = (app) => {
    setEditingNotes(app.Id);
    setNotesValue(app.CompanyNotes || '');
  };

  const saveNotes = async () => {
    if (editingNotes == null) return;
    const app = applications.find((a) => a.Id === editingNotes);
    if (!app) return;
    try {
      await applicationService.updateApplicationReview({
        Id: app.Id,
        IsShortlisted: app.IsShortlisted,
        CompanyNotes: notesValue,
      });
      setEditingNotes(null);
      setNotesValue('');
      loadData();
    } catch (error) {
      alert('Error saving notes: ' + error.message);
    }
  };

  const cancelEditNotes = () => {
    setEditingNotes(null);
    setNotesValue('');
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');
  const formatFileName = (path) => (path ? path.split(/[/\\]/).pop() : '-');

  if (!companyId) {
    return (
      <div className="company-applications">
        <div className="page-header">
          <h1>Applications</h1>
        </div>
        <p>Company session not found. Please log in again.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="company-applications">
        <div className="page-header">
          <h1>Applications</h1>
        </div>
        <p>Loading applications...</p>
      </div>
    );
  }

  return (
    <div className="company-applications">
      <div className="page-header">
        <h1>Applications</h1>
        <p>Review applicants and shortlist candidates</p>
      </div>
      <div className="applications-table-wrap">
        {applications.length === 0 ? (
          <p className="no-data">No applications received yet.</p>
        ) : (
          <div className="application-cards">
            {applications.map((a) => (
              <div key={a.Id} className="application-card">
                <div className="app-row-header" onClick={() => setExpandedId(expandedId === a.Id ? null : a.Id)}>
                  <div className="app-info">
                    <strong>{a.Student?.Name}</strong> ({a.Student?.Code}) – {a.Vacancy?.Title}
                  </div>
                  <span className={`shortlist-badge ${a.IsShortlisted ? 'yes' : 'no'}`}>
                    {a.IsShortlisted ? 'Shortlisted' : 'Not shortlisted'}
                  </span>
                </div>
                {expandedId === a.Id && (
                  <div className="app-details">
                    <p><strong>Email:</strong> {a.Student?.Email}</p>
                    <p><strong>Mobile:</strong> {a.Student?.MobileNo}</p>
                    <p><strong>Date:</strong> {formatDate(a.Date)}</p>
                    <p>
                      <strong>Resume:</strong> {formatFileName(a.Resume)}
                      {a.Resume && (
                        <button
                          type="button"
                          className="btn-download-pdf"
                          onClick={() => applicationService.downloadResume(a.Id, formatFileName(a.Resume))}
                        >
                          Download PDF
                        </button>
                      )}
                    </p>
                    {a.Description && <p><strong>Description:</strong> {a.Description}</p>}
                    {a.Answer1 && <p><strong>Answer 1:</strong> {a.Answer1}</p>}
                    {a.Answer2 && <p><strong>Answer 2:</strong> {a.Answer2}</p>}
                    {a.Answer3 && <p><strong>Answer 3:</strong> {a.Answer3}</p>}
                    <div className="app-actions">
                      {shortlistingId === a.Id ? (
                        <div className="shortlist-remarks-form">
                          <label className="remarks-label"><strong>Remarks</strong></label>
                          <textarea
                            value={shortlistRemarks}
                            onChange={(e) => setShortlistRemarks(e.target.value)}
                            rows={3}
                            placeholder="Enter remarks for shortlisting this applicant"
                          />
                          <div className="notes-btns">
                            <button className="btn-save" onClick={confirmShortlist}>Confirm Shortlist</button>
                            <button className="btn-cancel" onClick={cancelShortlist}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <button
                          className={`btn-shortlist ${a.IsShortlisted ? 'shortlisted' : ''}`}
                          onClick={() => handleShortlistClick(a)}
                        >
                          {a.IsShortlisted ? 'Remove from shortlist' : 'Shortlist'}
                        </button>
                      )}
                      {editingNotes === a.Id ? (
                        <div className="notes-edit">
                          <textarea
                            value={notesValue}
                            onChange={(e) => setNotesValue(e.target.value)}
                            rows={3}
                            placeholder="Add notes about this applicant"
                          />
                          <div className="notes-btns">
                            <button className="btn-save" onClick={saveNotes}>Save</button>
                            <button className="btn-cancel" onClick={cancelEditNotes}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="notes-view">
                          {a.CompanyNotes && <p><strong>Remarks:</strong> {a.CompanyNotes}</p>}
                          <button className="btn-notes" onClick={() => startEditNotes(a)}>
                            {a.CompanyNotes ? 'Edit remarks' : 'Add remarks'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanyApplications;
